import { createHomeframeRouter, HomeframeInput } from '@builtbyted/homeframe';
import React, { useEffect, useState } from 'react';
import {
  passkeyRequest,
  passkeyStatus,
  PasskeyStatus,
  usePasskey,
} from 'Utilities/passkeys';
import styles from './SignIn.css';

interface Credential {
  id: string;
  name: string;
  createdAt: string;
}

// A fragment is never sent to the server, proxy access log, or referrer. Remove
// the one-time enrollment link from visible browser history immediately.
const enrollmentToken =
  new URLSearchParams(window.location.hash.slice(1)).get('enroll') ?? undefined;
if (enrollmentToken) {
  const router = createHomeframeRouter(
    [{ id: 'login', path: '*', element: null }],
    { historyMode: 'browser' }
  );
  void router.navigate(window.location.pathname + window.location.search, {
    replace: true,
  });
}
const managing =
  new URLSearchParams(window.location.search).get('manage') === 'true';

export default function PasskeySignIn({ urlBase }: { urlBase: string }) {
  const [status, setStatus] = useState<PasskeyStatus>();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [name, setName] = useState('My passkey');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    passkeyStatus()
      .then(setStatus)
      .catch(() =>
        setError(
          'Unable to reach Showrunner. Try again when you are connected.'
        )
      );
  }, []);

  useEffect(() => {
    if (status?.authenticated && managing)
      passkeyRequest<Credential[]>('credentials')
        .then(setCredentials)
        .catch(() => setError('Unable to load your passkeys.'));
  }, [status]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.name === 'NotAllowedError'
            ? 'Passkey prompt canceled or timed out. Please try again.'
            : failure.message
          : 'Unable to use this passkey.'
      );
    } finally {
      setBusy(false);
    }
  };

  const signIn = () =>
    run(async () => {
      const next = await usePasskey();
      setStatus(next);
      if (!managing) window.location.assign(`${urlBase}/`);
    });

  const add = () =>
    run(async () => {
      setStatus(
        await usePasskey({
          token: enrolled ? undefined : enrollmentToken,
          name,
        })
      );
      setEnrolled(true);
      if (managing) setCredentials(await passkeyRequest('credentials'));
      if (!managing) window.location.assign(`${urlBase}/`);
    });

  return (
    <section
      className={styles.form}
      aria-label={managing ? 'Manage passkeys' : 'Passkey sign in'}
    >
      <h1>
        {managing
          ? 'Your passkeys'
          : enrollmentToken && !enrolled
          ? 'Set up your Showrunner passkey'
          : 'Sign in to Showrunner'}
      </h1>
      <p>
        Use your device’s fingerprint, face recognition, screen lock, or
        security key.
      </p>
      {status && !status.hasPasskeys && !enrollmentToken ? (
        <p>
          Owner setup is required. Open the private enrollment link supplied by
          your administrator.
        </p>
      ) : null}
      {status?.hasPasskeys || managing ? (
        <button type="button" disabled={busy} onClick={signIn}>
          {busy
            ? 'Waiting for your device…'
            : status?.authenticated
            ? 'Verify with passkey again'
            : 'Sign in with a passkey'}
        </button>
      ) : null}
      {(enrollmentToken && !enrolled) || (managing && status?.authenticated) ? (
        <>
          <label htmlFor="passkey-name">Passkey name</label>
          <HomeframeInput
            id="passkey-name"
            className={styles.textInput}
            value={name}
            maxLength={60}
            onChange={(event) => setName(event.target.value)}
            autoComplete="off"
          />
          <button type="button" disabled={busy || !status} onClick={add}>
            {busy ? 'Waiting for your device…' : 'Create a passkey'}
          </button>
        </>
      ) : null}
      {managing && status?.authenticated ? (
        <>
          <p>
            Add a second passkey or use a synced passkey provider so you can
            sign in from another device.
          </p>
          <ul>
            {credentials.map((credential) => (
              <li key={credential.id}>
                {credential.name}{' '}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await passkeyRequest(
                        `credentials/${encodeURIComponent(credential.id)}`,
                        undefined,
                        'DELETE'
                      );
                      setCredentials(await passkeyRequest('credentials'));
                    })
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <a href={`${urlBase}/`}>Return to Showrunner</a>
        </>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}
      {!status && !error ? <p role="status">Connecting…</p> : null}
    </section>
  );
}
